import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Button } from '@/shared/components/ui/button';
import { Calendar } from '@/shared/components/ui/Calendar';
import { cn } from '@/core/lib/utils';
import { format as formatDate } from 'date-fns';
import { parseApiDate, toApiDate } from '@/shared/utils/dateUtils';
import { useDateFormat } from '@/shared/hooks/useDateFormat';

export interface DatePickerProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  className?: string;
  'data-testid'?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  disabled = false,
  minDate,
  className,
  'data-testid': testId,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  // The configured token format, which `formatDisplayDate` ignored in favour of the browser's
  // locale. Deliberately *not* converting to the configured timezone: what this picker stores is
  // browser-local midnight serialised through `toISOString`, a floating date in instant's
  // clothing, and both the write path and the calendar highlight read it back browser-local.
  // Converting only the label made it name a different day from the calendar under it.
  const { dateFormat } = useDateFormat();

  // Parse UTC date from API for display in local timezone
  const selectedDate = parseApiDate(value) ?? undefined;

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Convert local date selection to UTC ISO string for API
      onChange(toApiDate(date));
    } else {
      onChange(null);
    }
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('w-full justify-start text-left font-normal', className)}
          disabled={disabled}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? formatDate(selectedDate, dateFormat) : placeholder}
          {value && (
            <button type="button" onClick={handleClear} className="ml-auto hover:text-destructive">
              ×
            </button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          selected={selectedDate}
          onSelect={handleSelect}
          fromDate={minDate}
          mode="single"
        />
      </PopoverContent>
    </Popover>
  );
}
